import {
  Bar,
  Button,
  Dialog,
  FlexBox,
  FlexBoxAlignItems,
  FlexBoxJustifyContent,
  InputDomRef,
  Label,
  Text,
  Title
} from '@ui5/webcomponents-react';
import { ResultUserMessage } from 'components/shared/UserMessage';
import {
  BaseQuery,
  TranslationType,
  useSendEmailNotificationMutation,
  useUpdateAssigneeMutation
} from 'generated/graphql';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { validateEmail } from 'utils/validator';
import { env } from 'config/env';
import { useStyles } from './AssignDialog.jss';
import { useEmailTemplate } from 'hooks/useEmailTemplate';
import { createPortal } from 'react-dom';
import { CopyEmailContentToClipboardDialog } from './CopyEmailContentToClipboardDialog/CopyEmailContentToClipboardDialog';
import { getEncodedMailtoTemplate, isAbleToSendNotificationEmail } from 'utils/notificationEmail';
import { MsalEmailInputSearch } from 'components/shared/MsalEmailInputSearch';
import { mapToAbapInputUpdate } from 'mappers/AbapInputUpdateMapper';
import { mapToNonAbapInputUpdate } from 'mappers/NonAbapInputMapper';
import { mapToNotesInputUpdate } from 'mappers/NotesInputUpdateMapper';
import { mapToDitaInputUpdate } from 'mappers/DitaInputUpdateMapper';
import { mapToTrainingInputUpdate } from 'mappers/TrainingUpdateMapper';
import { mapToCorporateContentInputUpdate } from 'mappers/CorporateContentInputUpdateMapper';
import { mapToMarketingInputUpdate } from 'mappers/MarketingInputUpdateMapper';

import ButtonDesign from '@ui5/webcomponents/dist/types/ButtonDesign.js';
import ValueState from '@ui5/webcomponents-base/dist/types/ValueState.js';
import WrappingType from '@ui5/webcomponents/dist/types/WrappingType.js';

export type AssignDialogProps = {
  isDialogOpen: boolean;
  queries: BaseQuery[];
  setIsDialogOpen: (isOpen: boolean) => void;
  setResultMessage: (result: ResultUserMessage) => void;
};

export const AssignDialog: React.FC<AssignDialogProps> = (props) => {
  const { isDialogOpen, queries, setIsDialogOpen, setResultMessage } = props;
  const { t } = useTranslation();
  const t_interval = (tag: string, count: number) => t(tag, { postProcess: 'interval', count });
  const classes = useStyles();
  const [email, setEmail] = useState('');
  const [emailForWindowsHandler, setEmailForWindowsHandler] = useState<string>('');
  const [, updateAssignee] = useUpdateAssigneeMutation();
  const [emailValueState, setEmailValueState] = useState<ValueState>(ValueState.None);
  const [, sendEmail] = useSendEmailNotificationMutation();
  const isEmailThroughBackend: boolean = env?.REACT_APP_EMAIL_VIA_BACKEND === 'true' ? true : false;
  const inputRef = useRef<InputDomRef>(null);
  const { getEmailBody } = useEmailTemplate();
  const [hasExceededEmailLengthLimit, setHasExceededEmailLengthLimit] = useState<boolean>(false);

  useEffect(() => {
    if (isDialogOpen) {
      setTimeout(() => {
        focusOnEmail();
      }, 0);
    }
  }, [isDialogOpen]);

  const clearStates = () => {
    setEmail('');
    setEmailValueState(ValueState.None);
  };

  const onChangeAssignee = (value: string) => {
    setEmail(value);
  };

  const clearAndCloseDialog = () => {
    clearStates();
    setIsDialogOpen(false);
  };

  const onAssign = async () => {
    const isEmailValid = validateEmail(email, setEmailValueState);
    if (isEmailValid) {
      await assign();
    } else {
      focusOnEmail();
    }
  };

  const focusOnEmail = () => {
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const onUpdateAssignee = async () => {
    const abapQueries = queries.filter((q) => q.translationType == TranslationType.ABAP);
    const nonAbapQueries = queries.filter((q) => q.translationType == TranslationType.NON_ABAP);
    const notesQueries = queries.filter((q) => q.translationType == TranslationType.NOTES);
    const marketingQueries = queries.filter((q) => q.translationType == TranslationType.MARKETING);
    const ditaQueries = queries.filter((q) => q.translationType == TranslationType.DITA);
    const trainingQueries = queries.filter((q) => q.translationType == TranslationType.TRAINING);
    const corporateContentQueries = queries.filter(
      (q) => q.translationType == TranslationType.CORPORATE_CONTENT
    );
    return updateAssignee({
      input: {
        ids: queries.map((q) => Number(q.id)),
        abapQueries: abapQueries.map((q) => mapToAbapInputUpdate(q, email)),
        nonAbapQueries: nonAbapQueries.map((q) => mapToNonAbapInputUpdate(q, email)),
        notesQueries: notesQueries.map((q) => mapToNotesInputUpdate(q, email)),
        ditaQueries: ditaQueries.map((q) => mapToDitaInputUpdate(q, email)),
        trainingQueries: trainingQueries.map((q) => mapToTrainingInputUpdate(q, email)),
        corporateContentQueries: corporateContentQueries.map((q) =>
          mapToCorporateContentInputUpdate(q, email)
        ),
        marketingQueries: marketingQueries.map((q) => mapToMarketingInputUpdate(q, email))
      }
    });
  };

  const assign = async () => {
    const isValidEmail = validateEmail(email, setEmailValueState);
    if (!isValidEmail) {
      return;
    }
    const { data, error } = await onUpdateAssignee();

    if (data?.queries && !error) {
      setResultMessage({
        isSuccess: true,
        message: t_interval('assign-dialog-success_interval', queries.length)
      });
      clearAndCloseDialog();
      await sendEmailToAssignee();
    } else {
      setResultMessage({ isSuccess: false, message: t('assign-dialog-error-update-assignee') });
    }
  };

  const sendEmailToAssignee = async () => {
    if (isEmailThroughBackend) {
      await sendEmailToAssigneeBackend();
    } else {
      sendEmailToAssigneeFrontend();
    }
  };

  const compareByProjectName = (a: BaseQuery, b: BaseQuery) => {
    return a.project.projectName.localeCompare(b.project.projectName);
  };

  const getBody = () => {
    let emailTable = '';
    const queriesHref = `${location.origin}/queries`;
    const sortedQueries = [...queries].sort(compareByProjectName);

    sortedQueries.forEach((query) => {
      const href = `${location.origin}/queries/${query.id}`;
      emailTable += getEmailBody(query, href);
    });

    return t('email-body', { emailTable, queriesHref });
  };

  const sendEmailToAssigneeFrontend = () => {
    if (email) {
      const uri = getEncodedMailtoTemplate(email, getBody(), t('email-subject'));

      if (isAbleToSendNotificationEmail(uri)) {
        location.assign(uri);
      } else {
        setEmailForWindowsHandler(email);
        setHasExceededEmailLengthLimit(true);
      }
    }
  };

  const sendEmailToAssigneeBackend = async () => {
    if (email) {
      const { data, error } = await sendEmail({
        email: {
          to: email,
          body: getBody(),
          subject: t('email-subject')
        }
      });

      if (data?.result && !error) {
        setResultMessage({ isSuccess: true, message: t('email-success') });
      } else {
        setResultMessage({ isSuccess: false, message: t('email-fail') });
      }
    }

    clearStates();
  };

  return createPortal(
    <>
      <Dialog
        open={isDialogOpen}
        initialFocus="assign-email-assignee"
        onBeforeClose={() => setIsDialogOpen(false)}
        header={
          <Bar>
            <Title wrappingType="None">{t('assign-dialog-title')}</Title>
          </Bar>
        }
        footer={
          <Bar
            endContent={
              <>
                <Button
                  onClick={onAssign}
                  design={ButtonDesign.Emphasized}
                  id="assign-dialog-confirm-button"
                  key="assign-dialog-confirm-button"
                  data-testid="assign-dialog-confirm-button"
                >
                  {t('confirm')}
                </Button>
                <Button
                  onClick={clearAndCloseDialog}
                  id="assign-dialog-cancel-button"
                  key="assign-dialog-cancel-button"
                  data-testid="assign-dialog-cancel-button"
                >
                  {t('cancel')}
                </Button>
              </>
            }
          />
        }
      >
        <FlexBox
          justifyContent={FlexBoxJustifyContent.End}
          alignItems={FlexBoxAlignItems.Center}
          id="assign-email-assignee"
          key="assign-email-assignee"
        >
          <Label className={classes.label} wrappingType="None">
            {t('assign-dialog-email-label')}
          </Label>
          <MsalEmailInputSearch
            aria-labelledby="project-name-label"
            data-testid="assign-dialog-email"
            value={email}
            onEmailChange={onChangeAssignee}
            className={classes.input}
            showClearIcon
            onBlur={() => validateEmail(email, setEmailValueState)}
            placeholder={t('assign-dialog-email-placeholder')}
            valueState={emailValueState}
            valueStateMessage={
              <Label data-testid="assign-dialog-email-message" wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-assignee')}
              </Label>
            }
          />
        </FlexBox>
        {!isEmailThroughBackend && (
          <Text className={classes.emailWarningText}>{t('assign-dialog-email-notification')}</Text>
        )}
      </Dialog>
      <CopyEmailContentToClipboardDialog
        email={emailForWindowsHandler}
        content={getBody()}
        isOpen={hasExceededEmailLengthLimit}
        setIsOpen={setHasExceededEmailLengthLimit}
      />
    </>,
    document.body
  );
};
